"use client";

import Link from "next/link";
import Image from "next/image";
import { MapPin, Phone } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

export default function Footer() {
  const t = useTranslations("Footer");
  const locale = useLocale() as "hr" | "en";
  const homeHref = locale === "en" ? "/en/" : "/";

  return (
    <footer className="bg-[#111111]">
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-8 lg:px-10">
        <div className="flex flex-col gap-10 sm:flex-row sm:flex-wrap sm:gap-12 lg:flex-nowrap lg:items-start lg:justify-between">

          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href={homeHref} className="inline-block">
              <Image
                src="/img/logo.svg"
                alt="Saiyan Gym"
                width={77}
                height={29}
                className="brightness-0 invert"
              />
            </Link>
          </div>

          {/* Pronađi nas */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
              {t("findUsLabel")}
            </p>
            <p className="text-sm text-white/80">{t("hours1")}</p>
            <p className="text-sm text-white/80">{t("hours2")}</p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=%C4%86ira%20Cari%C4%87a%201%2C%20Dubrovnik"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 flex items-center gap-2 text-sm text-white underline underline-offset-2 hover:text-white/70 transition-colors"
            >
              <MapPin size={14} className="shrink-0 text-white/60" />
              Ćira Carića 1, Dubrovnik
            </a>
          </div>

          {/* Javi nam se */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
              {t("getInTouchLabel")}
            </p>
            <a
              href="tel:+385916022843"
              className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
            >
              <Phone size={14} className="shrink-0 text-white/60" />
              +385 91 602 2843
            </a>
          </div>

          {/* Posjeti nas na */}
          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
              {t("followUsLabel")}
            </p>
            <div className="flex items-center gap-2">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/saiyan_gym_fitt/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("instagramAria")}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <defs>
                    <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
                      <stop offset="0%" stopColor="#fdf497" />
                      <stop offset="5%" stopColor="#fdf497" />
                      <stop offset="45%" stopColor="#fd5949" />
                      <stop offset="60%" stopColor="#d6249f" />
                      <stop offset="90%" stopColor="#285AEB" />
                    </radialGradient>
                  </defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="url(#ig-grad)" />
                  <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5" fill="none" />
                  <circle cx="17.5" cy="6.5" r="1" fill="white" />
                </svg>
              </a>
              {/* Facebook */}
              <a
                href="http://facebook.com/p/Saiyan-GYM-FITT-100092144478181"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("facebookAria")}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-[#1877F2] hover:bg-[#1877F2]/80 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M13.5 8.5H16L15.5 11H13.5V19H10.5V11H8.5V8.5H10.5V7C10.5 5.07 11.57 4 13.5 4H16V6.5H14.5C14 6.5 13.5 6.7 13.5 7.5V8.5Z" />
                </svg>
              </a>
            </div>
          </div>

        </div>

        <div className="mt-10 flex flex-col-reverse items-center gap-4 border-t border-white/10 pt-6 sm:flex-row sm:justify-between">
          <p className="text-xs text-white/40">
            {t("copyright", { year: new Date().getFullYear() })}
          </p>
          <div className="flex items-center gap-5">
            {/* Legal pages are Croatian-only for now — link stays unprefixed even from the /en tree. */}
            <Link
              href="/uvjeti-koristenja"
              className="text-xs text-white/60 underline underline-offset-2 hover:text-white transition-colors"
            >
              {t("termsLink")}
            </Link>
            <Link
              href="/politika-privatnosti"
              className="text-xs text-white/60 underline underline-offset-2 hover:text-white transition-colors"
            >
              {t("privacyLink")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
